import React, { useEffect, useState, Suspense, memo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { StarIcon, ArrowLeftIcon, FilterIcon, SearchIcon, ThumbsUpIcon, HeartIcon, AlertTriangleIcon, QuoteIcon, BarChart2Icon, ClockIcon, BookOpenIcon, MessageSquareIcon, Share2Icon, CheckCircleIcon } from 'lucide-react';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { useDarkMode } from '../context/DarkModeContext';
import { DisclosureBadgeGroup } from '../components/Disclosure/DisclosureBadgeGroup';
import { DisclosureFilterBar } from '../components/Disclosure/DisclosureFilterBar';
import { matchesDisclosureFilter, type DisclosureId } from '../data/disclosures';
// Mock data for book
const bookData = {
  id: '1',
  title: 'The Silent Echo',
  author: 'Elena Richards',
  series: 'Echo Series #1',
  coverImage: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.7,
  reviewCount: 124,
  genres: ['Thriller', 'Mystery', 'Psychological'],
  publicationDate: 'June 15, 2022'
};
/**
 * Weighted disclosure sets for the mock data. Most reviews carry NO badge —
 * absence of a badge means organic, which is what keeps a badge meaningful.
 * Combinations are included because multiple disclosures are legitimate.
 */
const DISCLOSURE_POOL: DisclosureId[][] = [[], [], [], [], [], [], ['purchased'], ['purchased'], ['verified-purchase'], ['verified-purchase'], ['arc'], ['arc'], ['arc', 'affiliate'], ['pr-copy'], ['paid-review'], ['paid-partnership'], ['arc', 'paid-partnership'], ['affiliate'], ['own-business']];
const pickDisclosures = (): DisclosureId[] => DISCLOSURE_POOL[Math.floor(Math.random() * DISCLOSURE_POOL.length)];

// Generate mock reviews with enhanced details
const generateMockReviews = (count: number) => {
  const names = ['Jane Doe', 'Alex Reader', 'Mark K.', 'Sarah L.', 'Paul T.', 'Emma W.', 'Michael B.', 'Lisa R.', 'David S.', 'Rachel M.'];
  const avatarColors = ['bg-blue-100 text-blue-700', 'bg-green-100 text-green-700', 'bg-indigo-100 text-indigo-700', 'bg-rose-100 text-rose-700', 'bg-amber-100 text-amber-700'];
  const moodOptions = ['Tense', 'Mysterious', 'Thought-provoking', 'Emotional', 'Atmospheric', 'Dark', 'Hopeful', 'Inspiring', 'Heartwarming', 'Melancholic', 'Adventurous', 'Funny', 'Romantic', 'Suspenseful'];
  const pacingOptions = ['Fast-paced', 'Slow burn', 'Medium-paced', 'Page-turner', 'Character-driven', 'Plot-driven', 'Steady pace', 'Builds slowly', 'Intense', 'Deliberate'];
  const warningOptions = ['Violence', 'Death', 'Grief', 'Mental health issues', 'Substance abuse', 'Sexual content', 'Strong language', 'Child abuse', 'Animal cruelty', 'Self-harm'];
  const comments = ['This book kept me on the edge of my seat the entire time. The characters were well-developed and the plot twists were unexpected. Highly recommend!', "Absolutely loved this book! The writing style was captivating and I couldn't put it down. Looking forward to reading more from this author.", 'A solid read with an interesting premise. Some parts dragged a bit, but overall I enjoyed it.', "One of the best books I've read this year. The author's attention to detail and world-building is incredible.", 'Great characters and plot development. The ending was a bit predictable, but I still enjoyed the journey.', 'I had high expectations for this book but was a bit disappointed. The pacing was off and I found it hard to connect with the characters.', "A masterpiece! The author has created a rich, immersive world that I didn't want to leave.", 'An entertaining read with some memorable moments, though I felt the middle section could have been tightened up a bit.', 'The premise was intriguing but the execution fell flat for me. The characters felt one-dimensional.', "I was completely absorbed in this story from start to finish. The author's writing style is beautiful and evocative."];
  const quotes = ['Sometimes the loudest echoes come from the words we never said.', 'The truth hides in the silence between heartbeats.', 'We are all just echoes of the people we once were.', 'Fear is just a shadow cast by courage.', 'Memory is the echo of a moment that refuses to be forgotten.', 'The past never truly dies; it just waits for the right moment to return.', 'Sometimes the only way forward is through the darkest part of yourself.', 'Trust is built in drops and lost in buckets.', 'The greatest mysteries are those we carry within ourselves.', "Time doesn't heal all wounds; it just teaches us how to live with the scars."];
  const reviews = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const randomDays = Math.floor(Math.random() * 90); // Random date within last 3 months
    const date = new Date(now);
    date.setDate(date.getDate() - randomDays);
    const rating = (Math.floor(Math.random() * 8) + 6) / 2; // Ratings between 3-5
    const nameIndex = Math.floor(Math.random() * names.length);
    const commentIndex = Math.floor(Math.random() * comments.length);
    const quoteIndex = Math.floor(Math.random() * quotes.length);
    const avatarColorIndex = Math.floor(Math.random() * avatarColors.length);
    // Generate random category ratings
    const categoryRatings = {
      writing: (Math.floor(Math.random() * 10) + 1) / 2,
      plot: (Math.floor(Math.random() * 10) + 1) / 2,
      pacing: (Math.floor(Math.random() * 10) + 1) / 2,
      characters: (Math.floor(Math.random() * 10) + 1) / 2,
      enjoyment: (Math.floor(Math.random() * 10) + 1) / 2
    };
    // Generate random tags
    const numMoodTags = Math.floor(Math.random() * 3) + 1;
    const moodTags = [];
    for (let j = 0; j < numMoodTags; j++) {
      const tag = moodOptions[Math.floor(Math.random() * moodOptions.length)];
      if (!moodTags.includes(tag)) moodTags.push(tag);
    }
    const numPacingTags = Math.floor(Math.random() * 2) + 1;
    const pacingTags = [];
    for (let j = 0; j < numPacingTags; j++) {
      const tag = pacingOptions[Math.floor(Math.random() * pacingOptions.length)];
      if (!pacingTags.includes(tag)) pacingTags.push(tag);
    }
    const hasWarnings = Math.random() > 0.5;
    const contentWarnings = [];
    if (hasWarnings) {
      const numWarnings = Math.floor(Math.random() * 2) + 1;
      for (let j = 0; j < numWarnings; j++) {
        const warning = warningOptions[Math.floor(Math.random() * warningOptions.length)];
        if (!contentWarnings.includes(warning)) contentWarnings.push(warning);
      }
    }
    const initials = names[nameIndex].split(' ').map(n => n[0]).join('');
    const isAuthorResponse = i === 2; // Make one review have an author response
    const isVerified = Math.random() > 0.3; // 70% chance of being VERIFIED REVIEWER
    // Commercial disclosure. Most reviews are organic (no badge at all); the
    // remainder spread across purchase, gifted, paid and affiliate so every
    // filter has something to show. Disclosure never influences the rating —
    // ratings above are generated independently of this.
    const disclosures = pickDisclosures();
    const helpfulCount = Math.floor(Math.random() * 20);
    const hasQuote = Math.random() > 0.5;
    const readingProgress = Math.random() > 0.7 ? Math.floor(Math.random() * 40) + 60 : 100; // 30% chance of DNF
    reviews.push({
      id: i + 1,
      name: names[nameIndex],
      initials,
      avatarColor: avatarColors[avatarColorIndex],
      rating,
      categoryRatings,
      date,
      comment: comments[commentIndex],
      verified: isVerified,
      disclosures,
      helpfulCount,
      moodTags,
      pacingTags,
      contentWarnings,
      favoriteQuote: hasQuote ? quotes[quoteIndex] : null,
      readingProgress,
      authorResponse: isAuthorResponse ? {
        text: "Thank you for your thoughtful review! I'm so glad you enjoyed the character development - that was something I really focused on while writing.",
        date: new Date(date.getTime() + 86400000 * 2) // 2 days after review
      } : null
    });
  }
  // Sort by date (newest first)
  return reviews.sort((a, b) => b.date.getTime() - a.date.getTime());
};
const formatDate = (date: Date) => {
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else if (diffDays < 30) {
    return `${Math.floor(diffDays / 7)} weeks ago`;
  } else if (diffDays < 365) {
    return `${Math.floor(diffDays / 30)} months ago`;
  } else {
    return `${Math.floor(diffDays / 365)} years ago`;
  }
};
const BookReviews = () => {
  const {
    id
  } = useParams();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    darkMode
  } = useDarkMode();
  const [book, setBook] = useState<any>(null);
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [moodFilter, setMoodFilter] = useState('all');
  const [pacingFilter, setPacingFilter] = useState('all');
  const [disclosureFilter, setDisclosureFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [helpfulReviews, setHelpfulReviews] = useState<number[]>([]);
  const reviewsPerPage = 5;
  useEffect(() => {
    // In a real app, you would fetch the book and reviews data here
    // For now, we'll use mock data
    setBook(bookData);
    setReviews(generateMockReviews(25)); // Generate 25 mock reviews
    setLoading(false);
  }, [id]);
  // Helper function to toggle helpful status
  const toggleHelpful = (reviewId: number) => {
    if (helpfulReviews.includes(reviewId)) {
      setHelpfulReviews(helpfulReviews.filter(id => id !== reviewId));
    } else {
      setHelpfulReviews([...helpfulReviews, reviewId]);
    }
  };
  // Filter reviews based on criteria
  const filteredReviews = reviews.filter(review => {
    // Rating filter
    if (filter === 'all') return true;
    if (filter === '5star') return review.rating >= 4.5;
    if (filter === '4star') return review.rating >= 3.5 && review.rating < 4.5;
    if (filter === '3star') return review.rating >= 2.5 && review.rating < 3.5;
    if (filter === '2star') return review.rating >= 1.5 && review.rating < 2.5;
    if (filter === '1star') return review.rating < 1.5;
    if (filter === 'withQuotes') return review.favoriteQuote;
    if (filter === 'dnf') return review.readingProgress < 100;
    return true;
  }).filter(review =>
  // Commercial relationship filter. Never subtractive by default: the
  // 'all' branch returns every review including paid, gifted and ARC.
  matchesDisclosureFilter(review.disclosures, disclosureFilter)).filter(review => {
    // Mood filter
    if (moodFilter === 'all') return true;
    return review.moodTags.includes(moodFilter);
  }).filter(review => {
    // Pacing filter
    if (pacingFilter === 'all') return true;
    return review.pacingTags.includes(pacingFilter);
  });
  // Sort reviews
  const sortedReviews = [...filteredReviews].sort((a, b) => {
    if (sortBy === 'newest') return b.date - a.date;
    if (sortBy === 'oldest') return a.date - b.date;
    if (sortBy === 'highest') return b.rating - a.rating;
    if (sortBy === 'lowest') return a.rating - b.rating;
    if (sortBy === 'helpful') return b.helpfulCount - a.helpfulCount;
    return 0;
  });
  // Paginate reviews
  const paginatedReviews = sortedReviews.slice((page - 1) * reviewsPerPage, page * reviewsPerPage);
  const totalPages = Math.ceil(filteredReviews.length / reviewsPerPage);
  // Get unique mood and pacing tags for filters
  const allMoodTags = [...new Set(reviews.flatMap(review => review.moodTags))].sort();
  const allPacingTags = [...new Set(reviews.flatMap(review => review.pacingTags))].sort();
  // Helper function to render rating bars
  const renderRatingBar = (rating: number, label: string) => <div className="flex items-center text-xs mb-1" data-id="element-8469" data-mp="p8bjq7y5itae">
      <span className={`w-20 ${dk.textMuted}`} data-id="element-8470" data-mp="p9mw2w5nx9d4">{label}</span>
      <div className={`h-2 w-24 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-full overflow-hidden`} data-id="element-8471" data-mp="taorf1wgl19t">
        <div className="h-full bg-amber-500 rounded-full" style={{
        width: `${rating / 5 * 100}%`
      }} data-id="element-8472" data-mp="yb2zgjy28b7u" />
      </div>
      <span className={`ml-2 ${dk.textMuted}`} data-id="element-8473" data-mp="1dctazapiv07x">{rating}</span>
    </div>;
  // Helper function to render star rating
  const renderStarRating = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    return <div className="flex text-amber-500" data-id="element-8474" data-mp="13g6oyilgm34b">
        {[...Array(5)].map((_, i) => <StarIcon key={i} className={`h-4 w-4 ${i < fullStars ? 'fill-current' : i === fullStars && hasHalfStar ? 'fill-current opacity-60' : 'stroke-current fill-transparent'}`} data-id="element-8475" data-mp="zeuzcd2l5hw4" data-mpi={i} />)}
        <span className={`ml-1 text-xs ${dk.textMuted}`} data-id="element-8476" data-mp="1mczjei6d5a08">{rating}</span>
      </div>;
  };
  if (loading) {
    return <div className={`flex items-center justify-center min-h-screen ${dk.page}`} data-id="element-8477" data-mp="1icfn1gtm6af5">
        <div className="text-center" data-id="element-8478" data-mp="2etsbrx3ujsmo">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" data-id="element-8479" data-mp="2eutqqcnzgz9o" />
          <p className={`mt-4 ${dk.textMuted}`} data-id="element-8480" data-mp="2a1ioypy138om">Loading reviews...</p>
        </div>
      </div>;
  }
  return <div className={dk.page} data-id="element-8481" data-mp="1un5bs64fyjcq">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-8482" data-mp="1zkey3cwjaewc">
        {/* Back Button */}
        <div className="mb-6" data-id="element-8483" data-mp="1lkm9po8hcarj">
          <Link to={`/book/${id}`} className={`inline-flex items-center ${rt.accentLink}`} data-id="element-8484" data-mp="1llfieur01acb">
            <ArrowLeftIcon className="h-4 w-4 mr-2" data-id="element-8485" data-mp="1pnseerfy1tvw" />
            Back to book
          </Link>
        </div>

        {/* Book Info */}
        <div className={`rounded-lg shadow-sm p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 ${dk.card}`} data-id="element-8486" data-mp="2iawmj5ac2obw">
          <img src={book.coverImage} alt={book.title} className="w-20 h-auto rounded shadow" data-id="element-8487" data-mp="24iq6dj76khcg" />
          <div data-id="element-8488" data-mp="2waeh61ab8vt6">
            <h1 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-8489" data-mp="2w9wb6cxuj382">
              {book.title}
            </h1>
            <p className={dk.textSecondary} data-id="element-8490" data-mp="311wgym0lkqck">by {book.author}</p>
            {book.series && <p className={`text-sm ${dk.textMuted}`} data-id="element-8491" data-mp="pkub7kywe5r7">{book.series}</p>}
            <div className="flex items-center mt-1" data-id="element-8492" data-mp="knccsoiib8xg">
              <div className="flex text-amber-500" data-id="element-8493" data-mp="kkq5mpzj2vfr">
                {[...Array(5)].map((_, i) => <StarIcon key={i} className={`h-4 w-4 ${i < Math.floor(book.rating) ? 'fill-current' : i < book.rating ? 'fill-current opacity-60' : 'stroke-current fill-transparent'}`} data-id="element-8494" data-mp="zdrwtzz6sau1" data-mpi={i} />)}
              </div>
              <span className={`text-sm ml-2 ${dk.textSecondary}`} data-id="element-8495" data-mp="1prlzbw6m0a7">
                {book.rating} ({book.reviewCount} reviews)
              </span>
            </div>
          </div>
          <div className="ml-auto mt-2 sm:mt-0" data-id="element-8496" data-mp="blbfjrn9wmvs">
            <button className={`px-4 py-2 rounded-md text-sm font-medium ${rt.primaryButton}`} data-id="element-8497" data-mp="1s0cpr4vb8uzp">
              Write a Review
            </button>
          </div>
        </div>

        {/* Filters Section */}
        <div className={`rounded-lg shadow-sm p-4 mb-6 ${dk.card}`} data-id="element-8498" data-mp="1u59ancd9vyja">
          <div className="flex flex-wrap items-center gap-4 mb-4" data-id="element-8499" data-mp="1z3korzalov7b">
            <div className="flex items-center" data-id="element-8500" data-mp="1z1q1wffrqt0l">
              <FilterIcon className={`h-5 w-5 mr-2 ${dk.textMuted}`} data-id="element-8501" data-mp="2424bw30xkd19" />
              <span className={`text-sm font-medium mr-3 ${dk.textSecondary}`} data-id="element-8502" data-mp="240t29mkt0b28">
                Filter by Rating:
              </span>
              <select value={filter} onChange={e => {
              setFilter(e.target.value);
              setPage(1); // Reset to first page when changing filter
            }} className={`border rounded-md text-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${dk.input}`} data-id="element-8503" data-mp="1bermzm7art4s">
                <option value="all" data-id="element-8504" data-mp="snqot0e041ma">All Ratings</option>
                <option value="5star" data-id="element-8505" data-mp="smz1sdygzc7m">5 Stars</option>
                <option value="4star" data-id="element-8506" data-mp="xm1xv8wh283g">4 Stars</option>
                <option value="3star" data-id="element-8507" data-mp="xkqsj4ndosa8">3 Stars</option>
                <option value="2star" data-id="element-8508" data-mp="3ax4rtt9xc0rf">2 Stars</option>
                <option value="1star" data-id="element-8509" data-mp="3auqvzi7f6kit">1 Star</option>
                <option value="withQuotes" data-id="element-8510" data-mp="36wb3iyskkjy0">With Quotes</option>
                <option value="dnf" data-id="element-8511" data-mp="31w6l3383pf55">Did Not Finish</option>
              </select>
            </div>
            <div className="flex items-center" data-id="element-8512" data-mp="2wx2coet03lfv">
              <span className={`text-sm font-medium mr-3 ${dk.textSecondary}`} data-id="element-8513" data-mp="2wwai97b53tr6">
                Sort by:
              </span>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)} className={`border rounded-md text-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${dk.input}`} data-id="element-8514" data-mp="2s03ki1swdokb">
                <option value="newest" data-id="element-8515" data-mp="2e7nd6osyr4dl">Newest First</option>
                <option value="oldest" data-id="element-8516" data-mp="297hqln1jw82d">Oldest First</option>
                <option value="highest" data-id="element-8517" data-mp="296zkzzt00hhs">Highest Rated</option>
                <option value="lowest" data-id="element-8518" data-mp="2484uscspbqnn">Lowest Rated</option>
                <option value="helpful" data-id="element-8519" data-mp="24ar2qdji886e">Most Helpful</option>
              </select>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4" data-id="element-8520" data-mp="yt9mfe2s8ru">
            <div className="flex items-center" data-id="element-8521" data-mp="y08btxvybh3">
              <HeartIcon className="h-4 w-4 text-rose-500 mr-2" data-id="element-8522" data-mp="3j4xrc90phfc3" />
              <span className={`text-sm font-medium mr-3 ${dk.textSecondary}`} data-id="element-8523" data-mp="3j6se7svjfhit">
                Mood:
              </span>
              <select value={moodFilter} onChange={e => {
              setMoodFilter(e.target.value);
              setPage(1);
            }} className={`border rounded-md text-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${dk.input}`} data-id="element-8524" data-mp="39euzwd98g9fv">
                <option value="all" data-id="element-8525" data-mp="35dt1fyn541ey">All Moods</option>
                {allMoodTags.map(tag => <option key={tag as string} value={tag as string} data-id="element-8526" data-mp="2qh1vt7mvhtgw">
                    {tag as string}
                  </option>)}
              </select>
            </div>
            <div className="flex items-center" data-id="element-8527" data-mp="2bu36pjbhnb5c">
              <BarChart2Icon className="h-4 w-4 text-blue-500 mr-2" data-id="element-8528" data-mp="2buv48zonbiy9" />
              <span className={`text-sm font-medium mr-3 ${dk.textSecondary}`} data-id="element-8529" data-mp="27t17v3lhh13w">
                Pacing:
              </span>
              <select value={pacingFilter} onChange={e => {
              setPacingFilter(e.target.value);
              setPage(1);
            }} className={`border rounded-md text-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${dk.input}`} data-id="element-8530" data-mp="2lov6mtsjma3a">
                <option value="all" data-id="element-8531" data-mp="34hz3eo125324">All Pacing</option>
                {allPacingTags.map(tag => <option key={tag as string} value={tag as string} data-id="element-8532" data-mp="2zjnssb2xuoiu">
                    {tag as string}
                  </option>)}
              </select>
            </div>
          </div>

          {/* Commercial relationship filter */}
          <div className={`mt-4 pt-4 border-t ${dk.border}`} data-id="element-8533" data-mp="39eza915qe9ua">
            <DisclosureFilterBar value={disclosureFilter} onChange={next => {
            setDisclosureFilter(next);
            setPage(1);
          }} items={reviews} data-id="element-8534" data-mp="3ly8xf1azyux9" />
          </div>
        </div>

        {/* Reviews Count */}
        <div className="flex items-center mb-4" data-id="element-8535" data-mp="3qzy64aaqywi6">
          <h2 className={`text-lg font-semibold ${dk.textHeading}`} data-id="element-8536" data-mp="3392af2367h5n">
            {filteredReviews.length}{' '}
            {filteredReviews.length === 1 ? 'Review' : 'Reviews'}
          </h2>
        </div>

        {/* Reviews List */}
        <div className="space-y-6" data-id="element-8537" data-mp="3c2e5ffkvvnht">
          {paginatedReviews.length > 0 ? paginatedReviews.map(review => <div key={review.id} className={`rounded-lg shadow-sm overflow-hidden ${dk.card}`} data-id="element-8538" data-mp="379cpd48xxhwn">
                <div className="p-4 sm:p-6" data-id="element-8539" data-mp="2fjzrzs0ppka3">
                  <div className="flex flex-col sm:flex-row gap-4" data-id="element-8540" data-mp="2fj7vmezajiir">
                    {/* Reviewer info */}
                    <div className="flex-shrink-0 flex flex-col items-center" data-id="element-8541" data-mp="25dgcro06wxow">
                      <div className={`h-12 w-12 rounded-full ${review.avatarColor} flex items-center justify-center font-medium`} data-id="element-8542" data-mp="2a7szq80vkr5e">
                        {review.initials}
                      </div>
                      <Link to={`/profile/${review.id}`} className={`text-sm font-medium mt-2 ${rt.accentLink}`} data-id="element-8543" data-mp="2o6aj5tjqzhhi">
                        {review.name}
                      </Link>
                      {/* Account verification — green is reserved for identity
                          and must never imply a verified purchase. */}
                      {review.verified && <span className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800'}`} data-id="element-8544" data-mp="12r4surepsgnw">
                          <CheckCircleIcon className="h-3 w-3" data-id="element-8545" data-mp="35etnrbie9eyw" />
                          Verified Reviewer
                        </span>}
                      {/* Commercial disclosure — a separate fact, never merged
                          with account verification above. */}
                      <DisclosureBadgeGroup disclosures={review.disclosures} subjectType="book" className="mt-1 justify-center" data-id="element-8546" data-mp="2qqpain241vni" />
                      {review.readingProgress < 100 && <div className={`mt-2 text-xs flex items-center ${dk.textMuted}`} data-id="element-8547" data-mp="2cqxtsek7m15t">
                          <BookOpenIcon className="h-3 w-3 mr-1" data-id="element-8548" data-mp="fxvxxy0ppzqx" />
                          {review.readingProgress}% read
                        </div>}
                    </div>

                    {/* Review content */}
                    <div className="flex-1" data-id="element-8549" data-mp="nhx0kvfcfk33">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-3" data-id="element-8550" data-mp="nglz529deof5">
                        <div data-id="element-8551" data-mp="s9yeels5tiv8">
                          {/* Overall rating */}
                          {renderStarRating(review.rating)}
                        </div>
                        <div className={`mt-2 sm:mt-0 text-xs flex items-center ${dk.textMuted}`} data-id="element-8552" data-mp="3vpvrb2hph4x9">
                          <ClockIcon className="h-3 w-3 mr-1" data-id="element-8553" data-mp="4l5bg5d4ear4" />
                          {formatDate(review.date)}
                        </div>
                      </div>

                      {/* Review text */}
                      <p className={`mb-3 ${dk.textSecondary}`} data-id="element-8554" data-mp="eevzxa98yl6t">
                        {review.comment}
                      </p>

                      {/* Favorite quote */}
                      {review.favoriteQuote && <div className={`mb-3 pl-3 border-l-2 italic flex ${dk.border} ${dk.textMuted}`} data-id="element-8555" data-mp="1nh7b14yrqpu4">
                          <QuoteIcon className={`h-4 w-4 mr-2 flex-shrink-0 mt-1 ${dk.textMuted}`} data-id="element-8556" data-mp="1ji8bjzvt3la2" />
                          <p className="text-sm" data-id="element-8557" data-mp="1sfsb94e0t9ik">"{review.favoriteQuote}"</p>
                        </div>}
                      {/* Tags section - Horizontal Layout */}
                      <div className="flex flex-wrap gap-y-2 mb-4" data-id="element-8558" data-mp="22fsxay39y3qg">
                        {/* Mood tags */}
                        {review.moodTags.length > 0 && <div className="flex items-center mr-4" data-id="element-8559" data-mp="2b7bbj3gnu9gw">
                            <HeartIcon className="h-3 w-3 text-rose-500 mr-1" data-id="element-8560" data-mp="2b838oipwof9a" />
                            <div className="flex flex-wrap gap-1" data-id="element-8561" data-mp="2q5dk5s2jtl50">
                              {review.moodTags.map((tag: string, i: number) => <span key={i} className={`text-xs px-2 py-1 ${rt.badgeRadius} ${darkMode ? 'bg-rose-900/20 text-rose-300' : 'bg-rose-50 text-rose-700'}`} data-id="element-8562" data-mp="34ratiylz2ssi" data-mpi={i}>
                                  {tag}
                                </span>)}
                            </div>
                          </div>}
                        {/* Pacing tags */}
                        {review.pacingTags.length > 0 && <div className="flex items-center mr-4" data-id="element-8563" data-mp="2pw4q8je9h82v">
                            <BarChart2Icon className="h-3 w-3 text-blue-500 mr-1" data-id="element-8564" data-mp="2c3wtgrmz9h46" />
                            <div className="flex flex-wrap gap-1" data-id="element-8565" data-mp="2h2rocry7yw4n">
                              {review.pacingTags.map((tag: string, i: number) => <span key={i} className={`text-xs px-2 py-1 ${rt.badgeRadius} ${darkMode ? 'bg-blue-900/20 text-blue-300' : 'bg-blue-50 text-blue-700'}`} data-id="element-8566" data-mp="2742791e5dfpg" data-mpi={i}>
                                    {tag}
                                  </span>)}
                            </div>
                          </div>}
                        {/* Content warnings */}
                        {review.contentWarnings.length > 0 && <div className="flex items-center" data-id="element-8567" data-mp="21z83ge4rf8sv">
                            <AlertTriangleIcon className="h-3 w-3 text-amber-500 mr-1" data-id="element-8568" data-mp="1wz2h9dh9eei6" />
                            <div className="flex flex-wrap gap-1" data-id="element-8569" data-mp="1s0r7f2qys3zy">
                              {review.contentWarnings.map((warning: string, i: number) => <span key={i} className={`text-xs px-2 py-1 ${rt.badgeRadius} ${darkMode ? 'bg-amber-900/20 text-amber-300' : 'bg-amber-50 text-amber-700'}`} data-id="element-8570" data-mp="gllt11hh0h0i" data-mpi={i}>
                                    {warning}
                                  </span>)}
                            </div>
                          </div>}
                      </div>
                      {/* Actions */}
                      <div className={`flex flex-wrap gap-3 mt-4 pt-3 border-t ${dk.border}`} data-id="element-8571" data-mp="3fy9vszhtof36">
                        <button className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-300 transition-colors" data-id="element-8572" data-mp="3kyef0xa47nx3">
                          <ThumbsUpIcon className="h-4 w-4" data-id="element-8573" data-mp="1qjv8mjmgd09s" />
                          Helpful ({review.helpfulCount})
                        </button>
                        <button className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-300 transition-colors" data-id="element-8574" data-mp="1vionafdh6xbj">
                          <MessageSquareIcon className="h-4 w-4" data-id="element-8575" data-mp="3sg43gmm0sne1" />
                          Comment
                        </button>
                        <button className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-300 transition-colors" data-id="element-8576" data-mp="b28ifi8qy14y">
                          <Share2Icon className="h-4 w-4" data-id="element-8577" data-mp="3ehcvzj1nxzyv" />
                          Share
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>) : <div className={`text-center py-8 rounded-lg shadow ${dk.card}`} data-id="element-8578" data-mp="3jf6086jeojwi">
              <p className={dk.textMuted} data-id="element-8579" data-mp="13ojt5lq2hifh">
                No reviews found matching your search.
              </p>
            </div>}
        </div>
      </div>
    </div>;
};
export default BookReviews;